import { createApi } from '@reduxjs/toolkit/query/react';
import { last, map, toInteger, toNumber, toString } from 'lodash';
import { useSelector } from 'react-redux';

import {
  applyFiltersToStatusItems,
  selectStatusItemsFilters,
  selectStatusItemsSorting,
  useStatusItemFilters,
} from './statusItemsSelectors';
import { createErrorHandler, ERROR_MESSAGES } from './errors';
import {
  Depot,
  HealthCheckStatusResponse,
  StatusItem,
  StatusItemsPayload,
} from '../types/IHealthCheck';
import { sortItems, sortOptionsForView } from '../utils/filterHelpers';

import { axiosBaseQuery } from '@/redux/axios';
import {
  StatusItemIssueType,
  StatusItemResponsibility,
} from '@/apps/health_check/types/enums';

export enum MuteEndCondition {
  // None = '0',
  HalfHour = '10',
  OneHour = '20',
  TwoHours = '30',
  EndOfBlock = '90',
}

export const statusItemsApi = createApi({
  reducerPath: 'statusItemsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['StatusItems', 'Depots'],
  endpoints: (builder) => ({
    getStatusItems: builder.query<
      HealthCheckStatusResponse,
      StatusItemsPayload
    >({
      query: ({
        ContractId,
        DepotId,
        IncludeHealthy,
        IncludeMuted,
        Responsibility,
      }: StatusItemsPayload) => {
        const searchParams = new URLSearchParams();
        if (ContractId.length > 0) {
          ContractId.forEach((id) => {
            searchParams.append('ContractId', id);
          });
        }
        if (DepotId.length > 0) {
          DepotId.forEach((id) => {
            searchParams.append('DepotId', id);
          });
        }
        searchParams.append('IncludeHealthy', IncludeHealthy.toString());
        searchParams.append('IncludeMuted', IncludeMuted.toString());
        // Sent as the numeric enum value: the API enum member is `Dispatch`,
        // not `Dispatcher`, so the name would fail to parse server-side and
        // silently apply no filter. Omitted entirely to get every phase.
        if (Responsibility !== undefined) {
          searchParams.append('Responsibility', Responsibility.toString());
        }
        return {
          url: 'StatusItems',
          method: 'get',
          params: searchParams,
        };
      },
      providesTags: ['StatusItems'],
      onQueryStarted: createErrorHandler(ERROR_MESSAGES.LOAD_FAILED),
    }),
    getDepots: builder.query<Depot[], void>({
      query: () => ({
        url: 'Depots',
        method: 'get',
      }),
      providesTags: ['Depots'],
      transformResponse: (data: { depots: Depot[] }) => data.depots,
      onQueryStarted: createErrorHandler(ERROR_MESSAGES.GET_DEPOTS_FAILED),
    }),
    muteVehicle: builder.mutation<
      void,
      {
        statusItemId: string | number;
        muteEndCondition: MuteEndCondition;
        healthCheckTypes: StatusItemIssueType[];
      }
    >({
      query: ({ statusItemId, muteEndCondition, healthCheckTypes }) => ({
        url: 'StatusItemsMuting',
        method: 'post',
        data: {
          statusItemId,
          muteEndCondition: toNumber(muteEndCondition),
          healthCheckTypes,
        },
      }),
      invalidatesTags: ['StatusItems'],
    }),
    unmuteVehicle: builder.mutation<void, { statusItemId: string | number }>({
      query: ({ statusItemId }) => ({
        url: `StatusItemsMuting/${statusItemId}`,
        method: 'delete',
      }),
      invalidatesTags: ['StatusItems'],
    }),
  }),
});

export const {
  useGetDepotsQuery,
  useMuteVehicleMutation,
  useUnmuteVehicleMutation,
} = statusItemsApi;
export default statusItemsApi;

// ----- RTK QUERY SELECTORS -----

export const useGetStatusItemsQuery = (
  responsibilityId?: StatusItemResponsibility
) => {
  const filters = useSelector(selectStatusItemsFilters);
  const statusItemFilters = useStatusItemFilters(responsibilityId);
  const sortOptions = useSelector(selectStatusItemsSorting);

  const sanitizedStatusItems = (items: StatusItem[]): StatusItem[] => {
    if (!items) {
      return [];
    }
    return map(items, (item) => ({
      ...item,
      vehicleId: item.vehicleInternalId
        ? toInteger(item.vehicleInternalId)
        : undefined,
    }));
  };

  const getActiveFilters = <T>(filters: (T | undefined)[]): NonNullable<T>[] =>
    filters.filter((filter): filter is NonNullable<T> => filter !== undefined);

  return statusItemsApi.useGetStatusItemsQuery(
    {
      ContractId: filters.flattenContracts.map((contract) =>
        toString(last(contract))
      ),
      DepotId: filters.depots,
      IncludeHealthy: filters.showHealthy,
      IncludeMuted: filters.showMuted,
      Responsibility: responsibilityId,
    },
    {
      selectFromResult: ({ data, ...otherFields }) => ({
        ...otherFields,
        data: sortItems(
          applyFiltersToStatusItems(
            sanitizedStatusItems(data?.statusItems || []),
            getActiveFilters([
              statusItemFilters.responsibilityId,
              statusItemFilters.muted,
              // statusItemFilters.healthy, // backend filter
              // statusItemFilters.contract, // backend filter
              statusItemFilters.severity,
              statusItemFilters.type,
              statusItemFilters.vehicleId,
              statusItemFilters.blockId,
            ])
          ),
          // Only the fields this view offers a control for; a sort it hides
          // must not order it either.
          sortOptionsForView(sortOptions, responsibilityId)
        ),
      }),
    }
  );
};
